import { StandardEvents } from '@shopify/events';
if (!customElements.get('pawships-model-viewer')) {
    let modelModule;
    const httpsURL = (value) => {
      try {
        const url = new URL(value);
        return url.protocol === 'https:' && !url.username && !url.password ? url.href : '';
      } catch { return ''; }
    };
    const loadModelModule = () => {
      if (customElements.get('model-viewer')) return Promise.resolve();
      if (!modelModule) {
        modelModule = import('https://cdn.jsdelivr.net/npm/@google/model-viewer@4.3.1/dist/model-viewer.min.js')
          .catch((error) => { modelModule = undefined; throw error; });
      }
      return modelModule;
    };

    class PawShipsModelViewer extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const options = { signal: this.controller.signal };
        this.stage = this.querySelector('[data-model-stage]');
        this.status = this.querySelector('[data-model-status]');
        this.retry = this.querySelector('[data-model-retry]');
        this.section = this.closest('.shopify-section');
        this.inline = this.querySelector('.pawships-model__inline');
        this.dialog = this.querySelector('[data-model-dialog]');
        this.expand = this.querySelector('[data-model-expand]');
        this.expand.addEventListener('click', () => {
          if (this.dialog.open) return;
          this.dialog.showModal();
          this.dialog.append(this.inline);
          this.previousOverflow = document.body.style.overflow;
          document.body.style.overflow = 'hidden';
          this.expanded = true;
        }, options);
        this.querySelector('[data-model-exit]').addEventListener('click', () => this.collapse(), options);
        this.dialog.addEventListener('close', () => this.collapse(), options);
        this.retry.addEventListener('click', () => this.load(), options);
        this.section?.addEventListener(StandardEvents.productSelect, (event) => {
          if (!this.ownsEvent(event)) return;
          const selection = this.selectionToken = (this.selectionToken || 0) + 1;
          this.release();
          this.hidden = true;
          event.promise.then(({ detail }) => {
            if (!this.isConnected || selection !== this.selectionToken) return;
            this.updateVariant({ target: event.target, detail: { resource: detail?.resource, data: detail } });
          }).catch(() => {
            if (!this.isConnected || selection !== this.selectionToken) return;
            this.hidden = true;
          });
        }, options);
        this.section?.addEventListener('variant:selected', (event) => {
          if (!this.ownsEvent(event)) return;
          this.release();
          this.hidden = true;
        }, options);
        this.section?.addEventListener('variant:update', (event) => this.updateVariant(event), options);
        this.refresh();
      }

      disconnectedCallback() {
        this.selectionToken = (this.selectionToken || 0) + 1;
        this.release();
        this.controller?.abort();
      }

      ownsEvent(event) {
        return event.target.closest?.('.shopify-section') === this.section &&
          !event.target.closest?.('quick-add-dialog, product-card');
      }

      updateVariant(event) {
        if (!this.ownsEvent(event)) return;
        const incoming = Array.from(event.detail?.data?.html?.querySelectorAll('pawships-model-viewer') || [])
          .find((node) => node.dataset.blockId === this.dataset.blockId);
        this.release();
        if (!incoming || !event.detail?.resource?.id) {
          this.hidden = true;
          return;
        }
        for (const key of ['productId', 'variantId', 'glb', 'usdz', 'title', 'poster', 'enabled']) {
          this.dataset[key] = incoming.dataset[key] || '';
        }
        this.querySelector('[role=region]').setAttribute('aria-label', this.dataset.title);
        this.refresh();
      }

      refresh() {
        this.hidden = this.dataset.enabled !== 'true' || !this.dataset.variantId || !httpsURL(this.dataset.glb);
        if (!this.hidden) this.load();
      }

      collapse() {
        if (!this.expanded) return;
        this.expanded = false;
        this.dialog.before(this.inline);
        if (this.dialog.open) this.dialog.close();
        document.body.style.overflow = this.previousOverflow;
        if (this.isConnected) this.expand.focus();
      }

      release() {
        this.collapse();
        this.loadToken = (this.loadToken || 0) + 1;
        clearTimeout(this.loadTimer);
        this.viewer = null;
        this.stage?.replaceChildren();
        this.stage?.setAttribute('aria-busy', 'false');
      }

      async load() {
        this.release();
        if (this.hidden || !this.isConnected) return;
        const token = this.loadToken;
        this.classList.remove('has-model-error');
        this.status.textContent = this.dataset.loading;
        this.retry.hidden = true;
        this.stage.setAttribute('aria-busy', 'true');
        const fail = () => {
          if (token !== this.loadToken || this.hidden) return;
          clearTimeout(this.loadTimer);
          this.loadToken += 1;
          this.viewer = null;
          this.stage.replaceChildren();
          this.stage.setAttribute('aria-busy', 'false');
          this.classList.add('has-model-error');
          this.status.textContent = this.dataset.error;
          this.retry.hidden = false;
        };
        this.loadTimer = setTimeout(fail, 45000);
        try {
          await loadModelModule();
          if (token !== this.loadToken || this.hidden || !this.isConnected) return;
          const viewer = this.querySelector('[data-model-template]').content.firstElementChild.cloneNode(true);
          viewer.setAttribute('src', httpsURL(this.dataset.glb));
          viewer.setAttribute('alt', this.dataset.title);
          const usdz = httpsURL(this.dataset.usdz);
          if (usdz) viewer.setAttribute('ios-src', usdz);
          if (this.dataset.poster) viewer.setAttribute('poster', this.dataset.poster);
          viewer.addEventListener('load', () => {
            if (token !== this.loadToken) return;
            clearTimeout(this.loadTimer);
            this.stage.setAttribute('aria-busy', 'false');
            this.status.textContent = this.dataset.ready;
          });
          viewer.addEventListener('error', fail);
          viewer.addEventListener('ar-status', (event) => {
            if (token === this.loadToken && event.detail.status === 'failed') {
              this.classList.add('has-model-error');
              this.status.textContent = this.dataset.arError;
            }
          });
          this.viewer = viewer;
          this.stage.replaceChildren(viewer);
        } catch { fail(); }
      }
    }
    customElements.define('pawships-model-viewer', PawShipsModelViewer);
  }
